// Port of lib/instructions.py — now the single source for BOTH editable prompts AND schemas.
//
// Python read INSTRUCTIONS_DIR/<name> from disk with an lru_cache. The static site bundles the
// whole instructions/ directory at build time via Vite's import.meta.glob (also handled by
// vitest), so the "cache" is the static module map below — and DROPPING A NEW FILE IN THE FOLDER
// is enough: no per-file import to register.
//
// Convention (owner mandate): every prompt is a `.md` file; every JSON schema is a `.json` file
// with the SAME basename as the instruction it belongs to. An instruction with no schema (e.g.
// the appended objectivity rules) simply has no sibling `.json`. Schemas are loaded through
// `load_schema`, which deep-clones the parsed JSON and resolves any `"$inject:<key>"` sentinels
// with runtime values (the age-gated trait options, the supported event categories, the sign
// list) — so the editable `.json` carries the full, hand-editable structure while the engine
// still fills the parts that must track live data.

import { INSTRUCTIONS_DIR } from "./config";

// Eagerly bundle every instruction (.md) and schema (.json) as raw text. The glob keys are the
// relative paths (".../instructions/extraction_fact_event.md"); we re-key them by basename.
const _md_modules = import.meta.glob("../../instructions/*.md", { query: "?raw", import: "default", eager: true }) as Record<string, string>;
const _json_modules = import.meta.glob("../../instructions/*.json", { query: "?raw", import: "default", eager: true }) as Record<string, string>;

function _by_basename(modules: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [path, text] of Object.entries(modules)) {
    const base = path.split("/").pop() as string;
    out[base] = text;
  }
  return out;
}

const _INSTRUCTIONS: Record<string, string> = _by_basename(_md_modules);
const _SCHEMAS: Record<string, string> = _by_basename(_json_modules);

/** The editable prompt text for `<name>` (e.g. "trait_chat.md"), trimmed. */
export function load_instruction(name: string): string {
  const text = _INSTRUCTIONS[name];
  if (text === undefined) {
    throw new Error(`Instruction file not found: ${INSTRUCTIONS_DIR}/${name}`);
  }
  return text.trim();
}

/**
 * The JSON schema paired with an instruction (e.g. "trait_chat.json"), parsed fresh on every
 * call (so callers can mutate the result freely) with every `"$inject:<key>"` sentinel resolved
 * from `replacements`. A sentinel with no matching key raises — a schema can never silently ship
 * a literal "$inject:..." string to the model.
 */
export function load_schema(name: string, replacements: Record<string, any> = {}): Record<string, any> {
  const raw = _SCHEMAS[name];
  if (raw === undefined) {
    throw new Error(`Schema file not found: ${INSTRUCTIONS_DIR}/${name}`);
  }
  return _resolve_injections(JSON.parse(raw), replacements, name);
}

const _INJECT_PREFIX = "$inject:";

function _resolve_injections(value: any, replacements: Record<string, any>, schema_name: string): any {
  if (typeof value === "string" && value.startsWith(_INJECT_PREFIX)) {
    const key = value.slice(_INJECT_PREFIX.length);
    if (!(key in replacements)) {
      throw new Error(`Schema ${schema_name} needs injection "${key}", but no value was supplied.`);
    }
    return replacements[key];
  }
  if (Array.isArray(value)) {
    return value.map((item) => _resolve_injections(item, replacements, schema_name));
  }
  if (value !== null && typeof value === "object") {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = _resolve_injections(v, replacements, schema_name);
    }
    return out;
  }
  return value;
}
